const version = require('../../package.json').version;

const assert = require('@barchart/common-js/lang/assert'),
	Disposable = require('@barchart/common-js/lang/Disposable'),
	Enum = require('@barchart/common-js/lang/Enum'),
	is = require('@barchart/common-js/lang/is');

const EndpointBuilder = require('@barchart/common-js/api/http/builders/EndpointBuilder'),
	Gateway = require('@barchart/common-js/api/http/Gateway'),
	FailureReason = require('@barchart/common-js/api/failures/FailureReason'),
	FailureType = require('@barchart/common-js/api/failures/FailureType'),
	ProtocolType = require('@barchart/common-js/api/http/definitions/ProtocolType'),
	ErrorInterceptor = require('@barchart/common-js/api/http/interceptors/ErrorInterceptor'),
	RequestInterceptor = require('@barchart/common-js/api/http/interceptors/RequestInterceptor'),
	ResponseInterceptor = require('@barchart/common-js/api/http/interceptors/ResponseInterceptor'),
	VerbType = require('@barchart/common-js/api/http/definitions/VerbType');

const Configuration = require('./../common/Configuration'),
	JwtProvider = require('./../security/JwtProvider');

const validate = require('./../data/validators/validate');

const RegistrationSchema = require('./../serialization/RegistrationSchema');

module.exports = (() => {
	'use strict';

	const REST_API_VERSION = 'v1';
	const REST_API_SECURE_PROTOCOL = 'https';
	const REST_API_SECURE_PORT = 443;

	/**
	 * The **central component of the SDK** used to interact with the API for
	 * the Barchart Engagement Service.
	 *
	 * @public
	 * @param {String} protocol - The protocol of the Barchart Engagement Service (https only).
	 * @param {String} host - The hostname of the Barchart Engagement Service service.
	 * @param {Number} port - The TCP port number of the Barchart Engagement Service service.
	 * @param {String} application - A code for the product which is using the gateway.
	 * @param {String} environment - A description of the environment we're connecting to.
	 * @extends {Disposable}
	 */
	class EngagementGateway extends Disposable {
		constructor(protocol, host, port, application, environment) {
			super();

			assert.argumentIsRequired(protocol, 'protocol', String);
			assert.argumentIsValid(protocol, 'protocol', p => parseProtocol(p) !== null, `must be one of [ ${Enum.getItems(ProtocolType).map(p => p.code).join(',')} ]`);
			assert.argumentIsRequired(host, 'host', String);
			assert.argumentIsRequired(port, 'port', Number);
			assert.argumentIsRequired(application, 'application', String);
			assert.argumentIsRequired(environment, 'environment', String);

			this._application = application;
			this._environment = environment;

			this._subscribers = [ ];

			this._jwtProvider = null;

			this._started = false;
			this._startPromise = null;

			const requestInterceptor = RequestInterceptor.fromDelegate((options, endpoint) => {
				return Promise.resolve()
					.then(() => {
						return this._jwtProvider.getToken()
							.then((token) => {
								options.headers = options.headers || {};
								options.headers.Authorization = `Bearer ${token}`;

								return options;
							});
					}).catch((e) => {
						const failure = FailureReason.forRequest({ endpoint })
							.addItem(FailureType.REQUEST_IDENTITY_FAILURE)
							.format();

						return Promise.reject(failure);
					});
			});

			const protocolType = Enum.fromCode(ProtocolType, protocol.toUpperCase());

			this._readRegistrationEndpoint = EndpointBuilder.for('read-registration', 'read registration')
				.withVerb(VerbType.GET)
				.withProtocol(protocolType)
				.withHost(host)
				.withPort(port)
				.withPathBuilder((pb) => {
					pb.withLiteralParameter('version', REST_API_VERSION)
						.withLiteralParameter('registrations', 'registrations')
						.withVariableParameter('application', 'application', 'application', false)
						.withVariableParameter('transport', 'transport', 'transport', false, t => t.code)
						.withVariableParameter('target', 'target', 'target', false)
						.withVariableParameter('account', 'account', 'account', true);
				})
				.withRequestInterceptor(requestInterceptor)
				.withRequestInterceptor(RequestInterceptor.PLAIN_TEXT_RESPONSE)
				.withResponseInterceptor(responseInterceptorForRegistrationDeserialization)
				.withErrorInterceptor(ErrorInterceptor.GENERAL)
				.endpoint;

			this._createRegistrationEndpoint = EndpointBuilder.for('create-registration', 'create registration')
				.withVerb(VerbType.POST)
				.withProtocol(protocolType)
				.withHost(host)
				.withPort(port)
				.withPathBuilder((pb) => {
					pb.withLiteralParameter('version', REST_API_VERSION)
						.withLiteralParameter('registrations', 'registrations');
				})
				.withQueryBuilder((qb) => {
					qb.withVariableParameter('force', 'force', 'force', false, f => f ? 'true' : 'false');
				})
				.withBodyBuilder((bb) => {
					bb.withVariableParameter('body', 'body', 'registration', false);
				})
				.withRequestInterceptor(requestInterceptor)
				.withRequestInterceptor(RequestInterceptor.PLAIN_TEXT_RESPONSE)
				.withResponseInterceptor(responseInterceptorForRegistrationDeserialization)
				.withErrorInterceptor(ErrorInterceptor.GENERAL)
				.endpoint;

			this._validateRemoteEndpoint = EndpointBuilder.for('validate-remote-address', 'validate remote address')
				.withVerb(VerbType.GET)
				.withProtocol(protocolType)
				.withHost(host)
				.withPort(port)
				.withPathBuilder((pb) => {
					pb.withLiteralParameter('version', REST_API_VERSION)
						.withLiteralParameter('remotes', 'remotes')
						.withVariableParameter('type', 'type', 'type', false, t => t.code)
						.withVariableParameter('address', 'address', 'address', false);
				})
				.withRequestInterceptor(RequestInterceptor.PLAIN_TEXT_RESPONSE)
				.withResponseInterceptor(ResponseInterceptor.DATA)
				.withErrorInterceptor(ErrorInterceptor.GENERAL)
				.endpoint;
		}

		/**
		 * Returns a description of the environment (e.g. development or production).
		 *
		 * @public
		 * @returns {String}
		 */
		get environment() {
			return this._environment;
		}

		/**
		 * Attempts to establish a connection to the backend. This function should be invoked
		 * immediately following instantiation. Once the resulting promise resolves, a
		 * connection has been established and other instance methods can be used.
		 *
		 * @public
		 * @async
		 * @param {JwtProvider} jwtProvider
		 * @returns {Promise<EngagementGateway>}
		 */
		async connect(jwtProvider) {
			return Promise.resolve()
				.then(() => {
					assert.argumentIsRequired(jwtProvider, 'jwtProvider', JwtProvider, 'JwtProvider');

					if (this._startPromise === null) {
						this._startPromise = Promise.resolve()
							.then(() => {
								this._started = true;

								this._jwtProvider = jwtProvider;

								return this;
							}).catch((e) => {
								this._started = false;
								this._startPromise = null;

								this._jwtProvider = null;

								throw e;
							});
					}

					return this._startPromise;
				});
		}

		/**
		 * Queries a user registration.
		 *
		 * @public
		 * @async
		 * @param {Object} query
		 * @param {String=} query.application
		 * @param {String=} query.account
		 * @param {Transport} query.transport
		 * @param {String} query.target
		 * @returns {Promise<Schema.Registration|null>}
		 */
		async readRegistration(query) {
			return Promise.resolve()
				.then(() => {
					checkStart.call(this);

					validate.registration.forQuery(query, 'query');

					const payload = { };

					payload.application = query.application || this._application;

					if (query.account) {
						payload.account = query.account;
					}

					payload.transport = query.transport;
					payload.target = query.target;

					return Gateway.invoke(this._readRegistrationEndpoint, payload)
						.catch((e) => {
							if (is.array(e) && e.length === 1 && is.object(e[0].value) && e[0].value.code === 'REGISTRATION_LOOKUP_FAILED_REGISTRATION_NOT_FOUND') {
								return null;
							}

							throw e;
						});
				});
		}

		/**
		 * Registers a callback which will be notified when the status of a
		 * registration changes. Soon after the subscription is first established,
		 * the existing state of the registration will be provided to the change
		 * callback.
		 * 
		 * @public
		 * @param {Object} query
		 * @param {String=} query.application
		 * @param {String=} query.account
		 * @param {Transport} query.transport
		 * @param {String} query.target
		 * @param {Callbacks.RegistrationMutatedCallback} changeCallback
		 * @returns {Disposable}
		 */
		subscribeRegistration(query, changeCallback) {
			checkStart.call(this);

			validate.registration.forQuery(query, 'query');

			const disposeCallback = (subscriber) => {
				delete this._subscribers[subscriber.key];
			};

			const subscriber = new RegistrationSubscriber(this, query, changeCallback, disposeCallback);
			subscriber.start();

			this._subscribers.push(subscriber);

			return Disposable.fromAction(() => subscriber.dispose());
		}
		
		/**
		 * Creates (or updates) a user registration.
		 *
		 * @public
		 * @async
		 * @param {Object} registration
		 * @param {String=} registration.application
		 * @param {String=} registration.account
		 * @param {Transport} registration.transport
		 * @param {String} registration.target
		 * @param {RegistrationStatus} registration.status
		 * @param {Boolean=} force - Causes a message to be sent to customer (even when the status is not changed).
		 * @returns {Promise<Schema.Registration>}
		 */
		async createRegistration(registration, force) {
			return Promise.resolve()
				.then(() => {
					checkStart.call(this);

					validate.registration.forCreate(registration, 'registration');

					assert.argumentIsOptional(force, 'force', Boolean);

					const payload = { };

					payload.force = is.boolean(force) && force;

					payload.registration = { };

					payload.registration.application = registration.application || this._application;

					if (registration.account) {
						payload.registration.account = registration.account;
					}

					payload.registration.transport = registration.transport;
					payload.registration.target = registration.target;
					payload.registration.status = registration.status;

					return Gateway.invoke(this._createRegistrationEndpoint, payload);
				});
		}

		/**
		 * Validates and formats a remote address (e.g. phone number, email address, etc).
		 *
		 * @public
		 * @async
		 * @param {Object} query
		 * @param {RemoteType} query.type
		 * @param {String} query.address
		 * @returns {Promise<Schema.RemoteValidation>}
		 */
		async validateRemote(query) {
			return Promise.resolve()
				.then(() => {
					checkStart.call(this);

					validate.remotes.forQuery(query, 'query');

					const payload = { };

					payload.type = query.type;
					payload.address = query.address;

					return Gateway.invoke(this._validateRemoteEndpoint, payload);
				});
		}

		/**
		 * Returns the version of the SDK.
		 *
		 * @public
		 * @static
		 * @returns {String}
		 */
		static get version() {
			return version;
		}

		/**
		 * Creates and starts a new {@link EngagementGateway} for use in the development environment.
		 *
		 * @public
		 * @static
		 * @async
		 * @param {JwtProvider} jwtProvider
		 * @param {String} application
		 * @returns {Promise<EngagementGateway>}
		 */
		static async forDevelopment(jwtProvider, application) {
			return Promise.resolve()
				.then(() => {
					assert.argumentIsRequired(jwtProvider, 'jwtProvider', JwtProvider, 'JwtProvider');
					assert.argumentIsRequired(application, 'application', String);

					return start(new EngagementGateway(REST_API_SECURE_PROTOCOL, Configuration.developmentHost, REST_API_SECURE_PORT, application, 'development'), jwtProvider);
				});
		}

		/**
		 * Creates and starts a new {@link EngagementGateway} for use in the staging environment.
		 *
		 * @public
		 * @static
		 * @async
		 * @param {JwtProvider} jwtProvider
		 * @param {String} application
		 * @returns {Promise<EngagementGateway>}
		 */
		static async forStaging(jwtProvider, application) {
			return Promise.resolve()
				.then(() => {
					assert.argumentIsRequired(jwtProvider, 'jwtProvider', JwtProvider, 'JwtProvider');
					assert.argumentIsRequired(application, 'application', String);

					return start(new EngagementGateway(REST_API_SECURE_PROTOCOL, Configuration.stagingHost, REST_API_SECURE_PORT, application, 'staging'), jwtProvider);
				});
		}

		/**
		 * Creates and starts a new {@link EngagementGateway} for use in the production environment.
		 *
		 * @public
		 * @static
		 * @async
		 * @param {JwtProvider} jwtProvider
		 * @param {String} application
		 * @returns {Promise<EngagementGateway>}
		 */
		static async forProduction(jwtProvider, application) {
			return Promise.resolve()
				.then(() => {
					assert.argumentIsRequired(jwtProvider, 'jwtProvider', JwtProvider, 'JwtProvider');
					assert.argumentIsRequired(application, 'application', String);

					return start(new EngagementGateway(REST_API_SECURE_PROTOCOL, Configuration.productionHost, REST_API_SECURE_PORT, application, 'production'), jwtProvider);
				});
		}

		_onDispose() {
			return;
		}

		toString() {
			return '[EngagementGateway]';
		}
	}

	/**
	 * @private
	 * @param {String} code
	 * @returns {ProtocolType|null}
	 */
	function parseProtocol(code) {
		return Enum.fromCode(ProtocolType, code.toUpperCase());
	}

	function start(gateway, jwtProvider) {
		return gateway.connect(jwtProvider)
			.then(() => {
				return gateway;
			});
	}

	function checkStart() {
		if (this.getIsDisposed()) {
			throw new Error('Unable to use gateway, the gateway has been disposed.');
		}

		if (!this._started) {
			throw new Error('Unable to use gateway, the gateway has not started.');
		}
	}

	const responseInterceptorForRegistrationDeserialization = ResponseInterceptor.fromDelegate((response, ignored) => {
		try {
			return JSON.parse(response.data, RegistrationSchema.CLIENT.schema.getReviver());
		} catch (e) {
			console.log('Error deserializing registration', e);

			throw e;
		}
	});

	class RegistrationSubscriber extends Disposable {
		constructor(parent, query, changeCallback, disposeCallback) {
			super();

			this._parent = parent;

			this._query = query;

			this._changeCallback = changeCallback;
			this._disposeCallback = disposeCallback;

			this._started = false;
			this._token = null;
		}

		start() {
			if (this._started) {
				return;
			}

			this._started = true;

			let previous = null;

			const poll = (interval) => {
				if (this.getIsDisposed()) {
					return;
				}

				const query = () => {
					if (this.getIsDisposed()) {
						return;
					}

					this._parent.readRegistration(this._query)
						.then((current) => {
							if (current === null) {
								return;
							}

							if (previous === null || previous.status !== current.status) {
								try {
									this._changeCallback(previous = current);
								} catch (e) {
									console.error('A user-defined callback for registration mutation threw an error.', e);
								}
							}
						}).catch((e) => {

						}).then(() => {
							poll(5000);
						});
				};

				this._token = setTimeout(query, interval);
			};

			poll(0);
		}

		_onDispose() {
			if (this._token !== null) {
				clearTimeout(this._token);
			}

			this._disposeCallback(this);
		}

		toString() {
			return '[RegistrationSubscriber]';
		}
	}

	return EngagementGateway;
})();
